import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, MapPin, ArrowRight, Printer } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";

const COLUMNS = [
  { id: "a-preparer", label: "À préparer", statuses: ["a_preparer"] },
  { id: "en-cours", label: "En cours", statuses: ["pret", "charge", "en_livraison"] },
  { id: "a-recuperer", label: "À récupérer / retour", statuses: ["livre", "retire", "a_recuperer"] },
  { id: "retournes", label: "Retournés / contrôlés", statuses: ["retourne", "controle"] },
];

const STATUS_LABELS = {
  a_preparer: "À préparer", pret: "Prêt", charge: "Chargé", en_livraison: "En livraison",
  livre: "Livré", retire: "Retiré (dépôt)", a_recuperer: "À récupérer", retourne: "Retourné", controle: "Contrôlé",
};

const nextStatus = (status, mode) => ({
  a_preparer: "pret",
  pret: mode === "livraison" ? "charge" : "retire",
  charge: "en_livraison",
  en_livraison: "livre",
  livre: "a_recuperer",
  retire: "a_recuperer",
  a_recuperer: "retourne",
}[status]);

export default function DeliveriesPage() {
  const [items, setItems] = useState(null);
  const [late, setLate] = useState(0);

  const load = () => api.get("/admin/logistics/overview")
    .then((r) => { setItems(r.data.items); setLate(r.data.late_returns); })
    .catch(() => toast.error("Vue logistique indisponible"));
  useEffect(() => { load(); }, []);

  const advance = async (item) => {
    const next = nextStatus(item.status, item.mode);
    if (!next) return;
    try {
      await api.post(`/admin/logistics/reservations/${item.reservation_id}/status`, { status: next });
      toast.success(`${item.ref} → ${STATUS_LABELS[next]}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!items) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="deliveries-loading">Chargement…</div>;

  return (
    <div data-testid="admin-deliveries-page">
      <p className="overline-ae">B08 — Livraisons &amp; Retraits</p>
      <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Livraisons &amp; Retraits</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Sorties et retours réels des réservations confirmées. Véhicule de référence : Volkswagen T-Cross.
        {late > 0 && <span className="ml-2 font-semibold text-red-600" data-testid="deliveries-late-warning">{late} retour(s) en retard — matériel bloqué jusqu'au contrôle.</span>}
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="deliveries-columns">
        {COLUMNS.map((col) => {
          const cards = items.filter((i) => col.statuses.includes(i.status));
          return (
            <div key={col.id} className="rounded-xl bg-cream p-3" data-testid={`deliveries-column-${col.id}`}>
              <p className="mb-3 flex items-center justify-between px-2 text-xs font-bold uppercase tracking-wider text-deepgreen">
                {col.label}
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px]">{cards.length}</span>
              </p>
              {cards.length === 0 ? (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">Aucune tournée</p>
              ) : cards.map((item) => (
                <div key={item.id} className="card-ae mb-3 p-4" data-testid={`delivery-card-${item.ref}`}>
                  <div className="flex items-center justify-between gap-2">
                    <Link to={`/admin/reservations/${item.reservation_id}`} className="text-sm font-semibold text-deepgreen hover:text-sage">{item.ref}</Link>
                    <span className="rounded-full bg-sage-light px-2 py-0.5 text-[10px] font-bold text-deepgreen">{STATUS_LABELS[item.status]}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{item.client || "—"} — {item.event_start} → {item.event_end}</p>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                    {item.mode === "livraison" ? <Truck className="h-3.5 w-3.5 text-sage" /> : <MapPin className="h-3.5 w-3.5 text-sage" />}
                    {item.mode === "livraison" ? `Livraison — ${item.address || "adresse à confirmer"}` : "Retrait dépôt Artres (gratuit, sur RDV)"}
                  </p>
                  {item.deposit_status && !["empreinte_active", "derogee", "liberee", "capturee", "capturee_partielle", "sans_caution"].includes(item.deposit_status) && (
                    <p className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-[10px] font-bold text-red-700" data-testid={`deposit-blocked-${item.ref}`}>En attente de caution — remise bloquée</p>
                  )}
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/logistics/reservations/${item.reservation_id}/prep-sheet`} target="_blank" rel="noreferrer"
                    className="btn-secondary-ae mt-2 w-full !py-2 !text-[11px]" data-testid={`prep-sheet-${item.ref}`}>
                    <Printer className="h-3.5 w-3.5" /> Bon de préparation
                  </a>
                  {item.status === "retourne" ? (
                    <Link to="/admin/retours" className="btn-primary-ae mt-3 w-full !py-2 !text-[11px]" data-testid={`delivery-control-${item.ref}`}>Contrôler le retour</Link>
                  ) : nextStatus(item.status, item.mode) ? (
                    <button type="button" onClick={() => advance(item)} className="btn-primary-ae mt-3 w-full !py-2 !text-[11px]" data-testid={`delivery-advance-${item.ref}`}>
                      {STATUS_LABELS[nextStatus(item.status, item.mode)]} <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {items.length === 0 && (
        <div className="mt-6">
          <EmptyState title="Aucune opération logistique">Les réservations confirmées apparaîtront ici automatiquement.</EmptyState>
        </div>
      )}
    </div>
  );
}
